import React, { useState } from "react";
import { Send, CheckCircle2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { submitConsultationToCRM } from "@/lib/crmTracking";

export interface LeadFormProps {
  title?: string;
  subtitle?: string;
  propertyTypeDefault?: string;
  compact?: boolean;
  onSuccess?: () => void;
}

export const LeadConsultationForm: React.FC<LeadFormProps> = ({
  title = "Request a Free Property Management Consultation",
  subtitle = "Discover how our technology-driven management maximizes your cash flow and minimizes hassle.",
  propertyTypeDefault = "Single Family",
  compact = false,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    propertyType: propertyTypeDefault,
    unitsCount: "1",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Submit lead data to the CRM (fire-and-forget).
    submitConsultationToCRM(
      formData,
      "feingold-consultation-form",
      "Property Management Consultation",
    );

    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      if (onSuccess) onSuccess();
    }, 600);
  };

  if (submitted) {
    return (
      <div
        className={`bg-white dark:bg-slate-900 border border-emerald-100 dark:border-emerald-950 rounded-2xl p-8 text-center shadow-lg ${compact ? "py-6" : "py-12"}`}
      >
        <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-10 h-10" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          Thank You!
        </h3>
        <p className="text-slate-600 dark:text-slate-300 max-w-md mx-auto mb-6 text-sm">
          Your consultation request has been received. A Feingold property
          specialist will analyze your property details and contact you within
          24 hours.
        </p>
        <Button
          variant="outline"
          onClick={() => {
            setSubmitted(false);
            setFormData({
              fullName: "",
              email: "",
              phone: "",
              address: "",
              propertyType: propertyTypeDefault,
              unitsCount: "1",
              message: "",
            });
          }}
          className="text-xs"
        >
          Submit Another Request
        </Button>
      </div>
    );
  }

  return (
    <div
      className={`bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-xl overflow-hidden ${compact ? "p-6" : "p-8 sm:p-10"}`}
    >
      {title && (
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
              {subtitle}
            </p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label
              htmlFor="fullName"
              className="text-xs font-semibold text-slate-700 dark:text-slate-300"
            >
              Full Name *
            </Label>
            <Input
              id="fullName"
              required
              placeholder="e.g. Sarah Jenkins"
              value={formData.fullName}
              onChange={(e) =>
                setFormData({ ...formData, fullName: e.target.value })
              }
              className="h-10 text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="email"
              className="text-xs font-semibold text-slate-700 dark:text-slate-300"
            >
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              required
              placeholder="sarah@example.com"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="h-10 text-sm"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label
              htmlFor="phone"
              className="text-xs font-semibold text-slate-700 dark:text-slate-300"
            >
              Phone Number *
            </Label>
            <Input
              id="phone"
              type="tel"
              required
              placeholder="(555) 000-0000"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              className="h-10 text-sm"
            />
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="propertyType"
              className="text-xs font-semibold text-slate-700 dark:text-slate-300"
            >
              Property Category
            </Label>
            <select
              id="propertyType"
              value={formData.propertyType}
              onChange={(e) =>
                setFormData({ ...formData, propertyType: e.target.value })
              }
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="Single Family">Single Family Home</option>
              <option value="Multi-Family">
                Multi-Family / Multifamily Unit
              </option>
              <option value="Commercial">Commercial / Retail / Office</option>
              <option value="Condo / Townhome">Condo / Townhome</option>
              <option value="Portfolio">Property Portfolio (Multiple)</option>
            </select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label
            htmlFor="address"
            className="text-xs font-semibold text-slate-700 dark:text-slate-300"
          >
            Property Address / City
          </Label>
          <Input
            id="address"
            placeholder="123 Main St, City, State"
            value={formData.address}
            onChange={(e) =>
              setFormData({ ...formData, address: e.target.value })
            }
            className="h-10 text-sm"
          />
        </div>

        <div className="space-y-1.5">
          <Label
            htmlFor="message"
            className="text-xs font-semibold text-slate-700 dark:text-slate-300"
          >
            How can we assist you?
          </Label>
          <Textarea
            id="message"
            rows={compact ? 2 : 3}
            placeholder="Tell us about your property management goals, current pain points, or timeline..."
            value={formData.message}
            onChange={(e) =>
              setFormData({ ...formData, message: e.target.value })
            }
            className="text-sm resize-none"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-11 bg-primary hover:bg-primary/90 text-white font-semibold shadow-md flex items-center justify-center gap-2 mt-2"
        >
          {loading ? (
            "Processing..."
          ) : (
            <>
              Submit Consultation Request <Send className="w-4 h-4" />
            </>
          )}
        </Button>

        <p className="text-[11px] text-slate-600 dark:text-slate-400 text-center flex items-center justify-center gap-1 mt-2">
          <ShieldAlert className="w-3.5 h-3.5 text-slate-500" /> Your
          information is strictly confidential and protected under privacy
          policies.
        </p>
      </form>
    </div>
  );
};
